// application/useCases/startFundingSource.js
import { canStartOn, latestStart } from '../../domain/services/fundingSourceDating';
import { EffectiveDateRejected } from '../ports/FundingSourceRepository.port';

/**
 * Starts a payer, as its first version on the date the user chose.
 *
 * A USE CASE FOR ONE RULE, deliberately, on the same terms as `retireFundingSource`. The rule is
 * policy — a payer starts today or earlier — and policy in the Firestore adapter is policy nobody can
 * test without Firestore, and that a second caller would quietly skip.
 *
 * NOT AN APPEND, and it cannot be one: `commitFundingSourceVersion` places a version on an existing
 * series and has a timeline to check against. There is no series yet, so nothing to collide with,
 * nothing to supersede, and no floor — the bound here is a ceiling instead, which is the one thing
 * `canTakeEffectAt` cannot express.
 *
 * BACKDATING STAYS OPEN, with no floor at all. A payer whose terms started in January has to be
 * enterable in August, because the projector resolves a service date to the config in force then.
 * The consequence is the one the timeline already carries: a backdated start covers dates the
 * projector has run over. See `fundingSourceDating` for why the other direction is closed.
 *
 * @param {{ clinicId: string, asOf: string, effectiveFrom: string, record: Object }} request - `asOf`
 *   comes from the edge rather than the clock, so the same input always produces the same decision.
 * @param {import('../ports/FundingSourceRepository.port').FundingSourceRepository} repository
 * @returns {Promise<string>} the new payer's `fundingSourceId`
 * @throws {EffectiveDateRejected} the date is later than today
 */
// eslint-disable-next-line import/prefer-default-export
export async function startFundingSource({
  clinicId, asOf, effectiveFrom, record,
}, repository) {
  if (!canStartOn(asOf, effectiveFrom)) {
    throw new EffectiveDateRejected(effectiveFrom, { latest: latestStart(asOf) });
  }

  return repository.createSeries({ clinicId, effectiveFrom, record });
} // startFundingSource()
